import classNames from 'classnames';
import { useSalesCounterpartLabel } from 'components/shared/compact-selector/use-toggle-button';
import { SelectSearchClient } from 'components/shared/select-search-client';
import { useCopyTextToClipboard } from 'hooks/useCopyToClipboard';
import { ISalesCounterpart } from 'models/ISalesCounterpart';

import './styles.scss';
import { RefObject, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type Props = {
  id?: string;
  onClientChanged: (client: Nullable<ISalesCounterpart>) => void;
  client: Nullable<ISalesCounterpart>;
  containerRef: RefObject<HTMLElement | null>;
  isDisabled?: boolean;
  iconName?: string;
};

export function ClientSelector({
  id,
  onClientChanged,
  client,
  containerRef,
  isDisabled,
  iconName = 'person_add',
}: Props) {
  const clientLabel = useSalesCounterpartLabel(client, false, 'form.Client.select');
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  useClickOutside([dropdownRef, buttonRef], () => setIsOpen(false), isOpen);

  const handleToggle = () => {
    if (!isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();

      setOpenUpward(window.innerHeight - rect.bottom < 300);
    }
    setIsOpen(o => !o);
  };

  const handleClientSelected = (selected: Nullable<ISalesCounterpart>) => {
    onClientChanged(selected);
    setIsOpen(false);
  };

  return (
    <>
      <div className="btn-group client-selector">
        <div className="d-flex client-and-copy-buttons">
          <button
            id={id}
            onClick={handleToggle}
            className="btn btn-flat-secondary btn-icon-start btn-client dropdown-toggle"
            type="button"
            aria-haspopup="true"
            aria-expanded={isOpen}
            disabled={isDisabled}
          >
            <span className={`${client ? 'text-info' : ''}`}>
              {client ? (
                <ClientSelectedIcon />
              ) : (
                <i className="icon icon-outlined" aria-hidden="true">
                  {iconName}
                </i>
              )}
            </span>
            {clientLabel}
          </button>
          {client && !isDisabled && <CopyButton client={client} />}
        </div>

        {client && !isDisabled && <ClearButton onClick={() => onClientChanged(null)} />}

        {isOpen &&
          containerRef.current &&
          createPortal(
            <ClientDropdown ref={dropdownRef} openUpward={openUpward}>
              <SelectSearchClient onClientSelected={handleClientSelected} />
            </ClientDropdown>,
            containerRef.current,
          )}
      </div>
    </>
  );
}

function useClickOutside(refs: RefObject<HTMLElement | null>[], onClickOutside: () => void, enabled: boolean) {
  const refsRef = useRef(refs);
  refsRef.current = refs;

  const callbackRef = useRef(onClickOutside);
  callbackRef.current = onClickOutside;

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideAnyRef = refsRef.current.some(ref => ref.current?.contains(target));
      if (!isInsideAnyRef) {
        callbackRef.current();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [enabled]);
}

type ClientDropdownProps = {
  ref: RefObject<HTMLDivElement | null>;
  openUpward: boolean;
  children: React.ReactNode;
};
function ClientDropdown({ ref, openUpward, children }: ClientDropdownProps) {
  return (
    <div ref={ref} className={classNames('client-selector-dropdown', { 'client-selector-dropdown--up': openUpward })}>
      {children}
    </div>
  );
}

function ClearButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      className="btn btn-flat-secondary btn-icon"
      style={{ width: '32px', height: '32px', padding: 0 }}
      title="Clear client"
      onClick={onClick}
    >
      <i className="icon icon-sm">cancel</i>
    </button>
  );
}

function CopyButton({ client }: { client: Nullable<ISalesCounterpart> }) {
  const copyToClipboard = useCopyTextToClipboard();

  return (
    <button
      className="btn btn-flat-secondary btn-icon copy-client-btn"
      onClick={() => copyToClipboard(`${client?.label!} (${client?.id!})`)}
    >
      <em className="icon">content_copy</em>
    </button>
  );
}

function ClientSelectedIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M10 17L13 14.06C12.61 14.02 12.32 14 12 14C9.33 14 4 15.34 4 18V20H13L10 17ZM12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12Z"
        fill="currentColor"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M16.47 20.5L13 17L14.4 15.59L16.47 17.67L21.6 12.5L23 13.91L16.47 20.5Z"
        fill="currentColor"
      />
    </svg>
  );
}
